import { supabase } from '../lib/supabase';

// ─────────────────────────────────────────────────────────────────────────────
// Consolida o gasto de APIs pagas por usuário: Lemit (cobrada em BRL) e
// geocodificação do Google (cobrada em USD). Mantemos as moedas separadas — não
// inventamos câmbio, para o número continuar auditável.
// ─────────────────────────────────────────────────────────────────────────────

export interface GastoUsuario {
    userId: string | null;
    nome: string;
    lemitConsultas: number;
    lemitCustoBrl: number;
    geocodeEnderecos: number;
    geocodeCustoUsd: number;
    ultimoUso: string | null;
}

/** Lê uma tabela inteira paginando (o PostgREST corta em 1000 por resposta). */
async function lerTudo(tabela: string, colunas: string, fromISO?: string) {
    const PAGE = 1000;
    const acc: any[] = [];
    for (let from = 0; ; from += PAGE) {
        let q = supabase.from(tabela).select(colunas).order('created_at', { ascending: false });
        if (fromISO) q = q.gte('created_at', fromISO);
        const { data, error } = await q.range(from, from + PAGE - 1);
        if (error) throw error;
        const rows = (data || []) as any[];
        acc.push(...rows);
        if (rows.length < PAGE) break;
    }
    return acc;
}

/**
 * Gasto por usuário no período. `source='api'` filtra as consultas Lemit que
 * realmente bateram na API — as servidas por cache não custam.
 */
export async function getGastoPorUsuario(fromISO?: string): Promise<GastoUsuario[]> {
    const [lemit, geo] = await Promise.all([
        lerTudo('lemit_consultas', 'consultado_por, consultado_por_nome, custo_estimado, source, created_at', fromISO),
        lerTudo('geocode_api_log', 'user_id, user_nome, enderecos, custo_estimado_usd, created_at', fromISO),
    ]);

    const mapa = new Map<string, GastoUsuario>();
    const pega = (id: string | null, nome?: string | null): GastoUsuario => {
        const k = String(id ?? 'desconhecido');
        if (!mapa.has(k)) {
            mapa.set(k, {
                userId: id ?? null, nome: nome || 'Desconhecido',
                lemitConsultas: 0, lemitCustoBrl: 0, geocodeEnderecos: 0, geocodeCustoUsd: 0, ultimoUso: null,
            });
        }
        const u = mapa.get(k)!;
        if (nome && u.nome === 'Desconhecido') u.nome = nome;
        return u;
    };
    const maisRecente = (a: string | null, b: string) => (!a || b > a ? b : a);

    lemit.forEach((r) => {
        if (r.source && r.source !== 'api') return;      // cache não gera custo
        const u = pega(r.consultado_por, r.consultado_por_nome);
        u.lemitConsultas++;
        u.lemitCustoBrl += Number(r.custo_estimado || 0);
        u.ultimoUso = maisRecente(u.ultimoUso, r.created_at);
    });

    geo.forEach((r) => {
        const u = pega(r.user_id, r.user_nome);
        u.geocodeEnderecos += Number(r.enderecos || 0);
        u.geocodeCustoUsd += Number(r.custo_estimado_usd || 0);
        u.ultimoUso = maisRecente(u.ultimoUso, r.created_at);
    });

    return Array.from(mapa.values()).sort((a, b) => (b.lemitCustoBrl - a.lemitCustoBrl) || (b.geocodeCustoUsd - a.geocodeCustoUsd));
}
